import { useRef, useState, useLayoutEffect } from 'react'
import { Package, Plus } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import {
  useCreatePackagingSupplier,
  useListPackagingSuppliersStats,
} from '@/shared/hooks/api/packagingSuppliers'
import { useGetUserSession } from '@/shared/hooks/api/auth'
import { useCanWriteOrgData } from '@/shared/hooks/useCanWriteOrgData'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Header } from '@/components/layout/header'
import { Main } from '@/components/layout/main'
import { ProfileDropdown } from '@/components/profile-dropdown'
import { Search } from '@/components/search'
import { ThemeSwitch } from '@/components/theme-switch'
import { FunnelStatItem } from './FunnelStatItem'
import { PackagingSuppliersStatCardSkeleton } from './PackagingSuppliersStatCardSkeleton'
import { PackagingSuppliersTable } from './PackagingSuppliersTable'
import { formatStatFraction } from './packagingSupplierStats'
import { resolveSuppliersListMount } from './suppliersListNavigation'

const STAT_CARD_COUNT = 4

export function PackagingSuppliersPage() {
  const { t } = useTranslation(['packagingSuppliers', 'common'])
  const navigate = useNavigate()
  const canWriteOrgData = useCanWriteOrgData()
  const { data: session } = useGetUserSession()
  const location = useLocation()
  const [searchParams, setSearchParams] = useSearchParams()
  const organizationId = session?.organizationId
  const listMountRef = useRef<ReturnType<typeof resolveSuppliersListMount> | null>(
    null
  )

  if (organizationId && listMountRef.current === null) {
    listMountRef.current = resolveSuppliersListMount(
      organizationId,
      location.state
    )
  }

  const listMount = listMountRef.current
  const legacyUrlParamsClearedRef = useRef(false)

  useLayoutEffect(() => {
    if (legacyUrlParamsClearedRef.current || searchParams.size === 0) {
      return
    }

    const legacyKeys = new Set(['page', 'pageSize', 'funnel', 'redFlags', 'filter'])
    const next = new URLSearchParams(searchParams)
    let changed = false

    for (const key of legacyKeys) {
      if (next.has(key)) {
        next.delete(key)
        changed = true
      }
    }

    legacyUrlParamsClearedRef.current = true

    if (changed) {
      setSearchParams(next, { replace: true })
    }
  }, [searchParams, setSearchParams])

  const { data: supplierStats, isLoading: isStatsLoading } =
    useListPackagingSuppliersStats()
  const createMutation = useCreatePackagingSupplier()
  const [isAddOpen, setIsAddOpen] = useState(false)
  const [name, setName] = useState('')

  const showEmptyState =
    !isStatsLoading && (supplierStats?.totalSuppliers ?? 0) === 0
  const showStatsSkeleton = !showEmptyState && isStatsLoading
  const showStats = !showEmptyState && !isStatsLoading

  const resetForm = () => {
    setName('')
  }

  const handleCreate = async () => {
    if (!name.trim()) {
      return
    }
    const created = await createMutation.mutateAsync({
      name: name.trim(),
    })
    setIsAddOpen(false)
    resetForm()
    navigate(`/packaging/suppliers/${created.id}`)
  }

  return (
    <>
      <Header fixed>
        <Search />
        <div className='ms-auto flex items-center space-x-4'>
          <ThemeSwitch />
          <ProfileDropdown />
        </div>
      </Header>

      <Main fixed className='flex min-h-0 flex-1 flex-col gap-4 px-6 sm:gap-6'>
        <div className='flex shrink-0 flex-wrap items-end justify-between gap-2'>
          <div>
            <h2 className='text-2xl font-bold tracking-tight'>
              {t('pages.suppliers.title')}
            </h2>
            <p className='text-muted-foreground'>
              {t('pages.suppliers.description')}
            </p>
          </div>
          {canWriteOrgData && (
            <Button onClick={() => setIsAddOpen(true)}>
              <Plus className='size-4' />
              {t('addSupplier')}
            </Button>
          )}
        </div>

        {showEmptyState && (
          <div className='flex flex-1 flex-col items-center justify-center gap-4 rounded-md border border-dashed py-16'>
            <Package className='size-10 text-muted-foreground/50' />
            <div className='space-y-1 text-center'>
              <h3 className='text-lg font-semibold'>{t('emptyState.title')}</h3>
              <p className='text-sm text-muted-foreground'>
                {t('emptyState.description')}
              </p>
            </div>
            {canWriteOrgData && (
              <Button onClick={() => setIsAddOpen(true)}>
                <Plus className='size-4' />
                {t('emptyState.action')}
              </Button>
            )}
          </div>
        )}

        {!showEmptyState && (
          <div className='flex min-h-0 flex-1 flex-col gap-4'>
            {showStatsSkeleton && (
              <div
                className='grid shrink-0 gap-2 sm:grid-cols-2 lg:grid-cols-4'
                aria-busy='true'
                aria-label={t('loading')}
              >
                {Array.from({ length: STAT_CARD_COUNT }, (_, index) => (
                  <PackagingSuppliersStatCardSkeleton key={index} />
                ))}
              </div>
            )}

            {showStats && supplierStats != null && (
              <div className='grid shrink-0 gap-2 sm:grid-cols-2 lg:grid-cols-4'>
                <FunnelStatItem
                  label={t('stats.suppliersOpened')}
                  percent={supplierStats.openedPercent}
                  description={formatStatFraction(
                    supplierStats.opened,
                    supplierStats.totalSuppliers
                  )}
                />
                <FunnelStatItem
                  label={t('stats.suppliersAcknowledged')}
                  percent={supplierStats.acknowledgedPercent}
                  description={formatStatFraction(
                    supplierStats.acknowledged,
                    supplierStats.totalSuppliers
                  )}
                />
                <FunnelStatItem
                  label={t('stats.suppliersWithPackagingItems')}
                  percent={supplierStats.withPackagingItemsPercent}
                  description={formatStatFraction(
                    supplierStats.withPackagingItems,
                    supplierStats.totalSuppliers
                  )}
                />
                <FunnelStatItem
                  label={t('stats.suppliersWithAllItemsComplete')}
                  percent={supplierStats.allItemsCompletePercent}
                  description={formatStatFraction(
                    supplierStats.allItemsComplete,
                    supplierStats.totalSuppliers
                  )}
                />
              </div>
            )}

            {organizationId && listMount && (
              <PackagingSuppliersTable initialListState={listMount.initialState} />
            )}
          </div>
        )}
      </Main>

      {canWriteOrgData && (
        <Dialog open={isAddOpen} onOpenChange={setIsAddOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('addSupplier')}</DialogTitle>
          </DialogHeader>
          <div className='space-y-1'>
            <Label htmlFor='supplier-name'>{t('form.name')}</Label>
            <Input
              id='supplier-name'
              value={name}
              onChange={(event) => setName(event.target.value)}
            />
          </div>
          <DialogFooter>
            <Button
              variant='outline'
              onClick={() => {
                setIsAddOpen(false)
                resetForm()
              }}
            >
              {t('common:cancel', { defaultValue: 'Cancel' })}
            </Button>
            <Button
              onClick={() => {
                void handleCreate()
              }}
              disabled={!name.trim() || createMutation.isPending}
            >
              {t('common:save', { defaultValue: 'Save' })}
            </Button>
          </DialogFooter>
        </DialogContent>
        </Dialog>
      )}
    </>
  )
}
